import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CalendarDays, Trash2, Clock } from "lucide-react";
import { format, isToday, isTomorrow, isPast } from "date-fns";

const PLATFORM_COLORS = {
    linkedin: "text-blue-400 border-blue-400/40",
    twitter: "text-sky-400 border-sky-400/40",
    reddit: "text-orange-400 border-orange-400/40",
    instagram: "text-pink-400 border-pink-400/40",
};

function dateLabel(date) {
    if (isToday(date)) return "Today";
    if (isTomorrow(date)) return "Tomorrow";
    return format(date, "EEE, MMM d");
}

export default function Calendar() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/calendar");
            setEvents(data);
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const deleteEvent = async (id) => {
        try {
            await api.delete(`/calendar/${id}`);
            setEvents(prev => prev.filter(e => e.id !== id));
            toast.success("Event removed");
        } catch {
            toast.error("Could not delete");
        }
    };

    const markPublished = async (id) => {
        try {
            await api.patch(`/calendar/${id}`, { status: "published" });
            setEvents(prev => prev.map(e => e.id === id ? { ...e, status: "published" } : e));
            toast.success("Marked as published");
        } catch {
            toast.error("Could not update");
        }
    };

    // Group events by date
    const grouped = events.reduce((acc, ev) => {
        const key = format(new Date(ev.scheduled_at), "yyyy-MM-dd");
        if (!acc[key]) acc[key] = [];
        acc[key].push(ev);
        return acc;
    }, {});

    return (
        <div className="min-h-screen">
            <Navbar />
            <div className="max-w-4xl mx-auto px-6 py-10">
                <div className="lk-label">Content Calendar</div>
                <h1 className="font-heading text-4xl sm:text-5xl mt-2 tracking-tight">Your scheduled posts</h1>
                <p className="text-[var(--lk-muted)] mt-3 max-w-xl">Schedule posts from the Workspace. Copy the text and publish manually on each platform.</p>

                {loading ? (
                    <div className="mt-10 text-[var(--lk-muted)]">Loading…</div>
                ) : err ? (
                    <div className="mt-10 text-red-400">{err}</div>
                ) : events.length === 0 ? (
                    <div className="mt-16 lk-border lk-surface rounded-xl p-10 text-center">
                        <CalendarDays className="w-10 h-10 text-[var(--lk-muted)] mx-auto mb-4" />
                        <div className="font-heading text-2xl">Nothing scheduled yet</div>
                        <div className="text-[var(--lk-muted)] mt-2 text-sm">Generate a post in Workspace and click "Schedule this post".</div>
                    </div>
                ) : (
                    <div className="mt-10 space-y-8">
                        {Object.entries(grouped).sort().map(([day, dayEvents]) => (
                            <div key={day}>
                                <div className="lk-label mb-3 flex items-center gap-2">
                                    <Clock className="w-3.5 h-3.5" />
                                    {dateLabel(new Date(day))} — {format(new Date(day), "MMM d, yyyy")}
                                </div>
                                <div className="space-y-3">
                                    {dayEvents.map(ev => {
                                        const colorCls = PLATFORM_COLORS[ev.platform] || "text-[var(--lk-primary)] border-[var(--lk-primary)]/40";
                                        const past = isPast(new Date(ev.scheduled_at));
                                        return (
                                            <div key={ev.id}
                                                className={`lk-border lk-surface rounded-xl p-5 ${ev.status === "published" ? "opacity-50" : ""}`}
                                                data-testid={`calendar-event-${ev.id}`}>
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <span className={`text-xs lk-border rounded-full px-2 py-0.5 capitalize ${colorCls}`}>
                                                                {ev.platform}
                                                            </span>
                                                            <span className="text-xs text-[var(--lk-muted)] font-mono">
                                                                {format(new Date(ev.scheduled_at), "h:mm a")}
                                                            </span>
                                                            {ev.status === "published" && (
                                                                <span className="text-xs text-emerald-400">Published</span>
                                                            )}
                                                        </div>
                                                        {ev.title && <div className="font-heading text-lg truncate">{ev.title}</div>}
                                                        {ev.draft_text && (
                                                            <div className="text-sm text-[var(--lk-muted)] mt-1 line-clamp-2 whitespace-pre-line">{ev.draft_text}</div>
                                                        )}
                                                    </div>
                                                    <div className="flex gap-2 shrink-0">
                                                        {ev.draft_text && (
                                                            <button
                                                                onClick={() => { navigator.clipboard.writeText(ev.draft_text); toast.success("Copied"); }}
                                                                className="lk-border rounded-lg px-3 py-1.5 text-xs text-[var(--lk-muted)] hover:text-[var(--lk-text)]">
                                                                Copy
                                                            </button>
                                                        )}
                                                        {ev.status !== "published" && (
                                                            <button onClick={() => markPublished(ev.id)}
                                                                className="lk-border rounded-lg px-3 py-1.5 text-xs text-emerald-400 border-emerald-400/40 hover:bg-emerald-400/10">
                                                                Mark published
                                                            </button>
                                                        )}
                                                        <button onClick={() => deleteEvent(ev.id)}
                                                            className="lk-border rounded-lg p-1.5 text-[var(--lk-muted)] hover:text-red-400">
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
